import { REFERENCE_FX_CURRENCY_CODES } from "@/config/currencies";
import { isValidLocale, type Locale } from "@/config/locale";

/** Current cookie name — bump when forcing a one-time reset of stored locale/channel prefs. */
export const GEO_PREF_COOKIE = "geo_pref_v2";

/** Previous name (until geo_pref_v2); middleware clears this so browsers drop stale JSON. */
export const LEGACY_GEO_PREF_COOKIE = "geo_pref";

/** Fired after geo preference cookie changes so client UI (e.g. cart pricing) can refetch. */
export const GEO_PREF_UPDATED_EVENT = "bergdorf-geo-pref-updated";

export function dispatchGeoPrefUpdated(): void {
	if (typeof window === "undefined") return;
	window.dispatchEvent(new Event(GEO_PREF_UPDATED_EVENT));
}

const REFERENCE_FX_SET = new Set<string>(REFERENCE_FX_CURRENCY_CODES);

export function isValidReferenceFxCurrencyCode(code: string | undefined | null): code is string {
	if (!code || typeof code !== "string") return false;
	return REFERENCE_FX_SET.has(code.trim().toUpperCase());
}

export function normalizeReferenceFxCurrencyCode(code: string | undefined | null): string | null {
	if (!code || typeof code !== "string") return null;
	const u = code.trim().toUpperCase();
	return REFERENCE_FX_SET.has(u) ? u : null;
}

export type ParsedGeoPref = {
	locale: Locale;
	channel: string;
	referenceCurrency?: string;
};

/**
 * Parse geo preference cookie JSON (`GEO_PREF_COOKIE`). Validates locale, channel, optional reference currency.
 */
export function parseGeoPrefCookieValue(raw: string | undefined | null): ParsedGeoPref | null {
	if (!raw || typeof raw !== "string") return null;
	try {
		const parsed = JSON.parse(raw) as {
			locale?: string;
			channel?: string;
			referenceCurrency?: string;
		};
		if (typeof parsed.locale !== "string" || !isValidLocale(parsed.locale)) return null;
		if (typeof parsed.channel !== "string" || !parsed.channel.trim()) return null;
		const ch = parsed.channel.trim().toLowerCase();

		const ref = normalizeReferenceFxCurrencyCode(parsed.referenceCurrency);
		const out: ParsedGeoPref = { locale: parsed.locale, channel: ch };
		if (ref) out.referenceCurrency = ref;
		return out;
	} catch {
		return null;
	}
}
